import { useEffect, useState } from "react";
import {
	Loader2,
	MoreHorizontal,
	Pencil,
	Plus,
	Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { cn } from "@/lib/utils";
import { PermissionGate } from "@/components/PermissionGate";
import { usePermission } from "@/hooks/usePermission";
import { SYSTEM_STAGES } from "@/components/tasks/system-stages";
import {
	createTaskSubStatus,
	deleteTaskSubStatus,
	listTaskSubStatuses,
	updateTaskSubStatus,
	type TaskSubStatus,
} from "@/services/task-sub-status.service";
import { SectionBlock } from "./SectionBlock";

const COLOR_SWATCHES = [
	"#64748b",
	"#0058be",
	"#006c49",
	"#f59e0b",
	"#dc2626",
	"#7c3aed",
	"#0ea5e9",
	"#ec4899",
];

const PARENT_OPTIONS = SYSTEM_STAGES.map((s) => ({
	value: s.key,
	label: s.name,
	color: s.color,
}));

function parentLabel(key: string): string {
	return SYSTEM_STAGES.find((s) => s.key === key)?.name ?? key;
}

function parentColor(key: string): string {
	return SYSTEM_STAGES.find((s) => s.key === key)?.color ?? "#64748b";
}

function Chip({ label, color }: { label: string; color: string }) {
	return (
		<span
			className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium"
			style={{
				backgroundColor: `${color}1a`,
				color,
				border: `1px solid ${color}33`,
			}}
		>
			<span
				className="h-2 w-2 rounded-full"
				style={{ backgroundColor: color }}
			/>
			{label}
		</span>
	);
}

type FormState = {
	name: string;
	parent_status: string;
	color: string;
};

const EMPTY_FORM: FormState = {
	name: "",
	parent_status: SYSTEM_STAGES[0].key,
	color: COLOR_SWATCHES[0],
};

function SubStatusDialog({
	open,
	onClose,
	editing,
	onSaved,
}: {
	open: boolean;
	onClose: () => void;
	editing: TaskSubStatus | null;
	onSaved: () => void;
}) {
	const [form, setForm] = useState<FormState>(EMPTY_FORM);
	const [saving, setSaving] = useState(false);
	const isEdit = editing !== null;

	useEffect(() => {
		if (!open) return;
		if (editing) {
			setForm({
				name: editing.name,
				parent_status: editing.parent_status,
				color: editing.color,
			});
		} else {
			setForm(EMPTY_FORM);
		}
	}, [editing, open]);

	async function handleSave() {
		const name = form.name.trim();
		if (!name) {
			toast.error("Name is required.");
			return;
		}

		setSaving(true);
		try {
			if (isEdit && editing) {
				await updateTaskSubStatus(editing.id, {
					name,
					parent_status: form.parent_status,
					color: form.color,
				});
				toast.success("Sub-status updated.");
			} else {
				await createTaskSubStatus({
					name,
					parent_status: form.parent_status,
					color: form.color,
				});
				toast.success("Sub-status created.");
			}
			onSaved();
			onClose();
		} catch (err) {
			toast.error(
				(err as Error).message || "Failed to save sub-status.",
			);
		} finally {
			setSaving(false);
		}
	}

	return (
		<Dialog open={open} onOpenChange={(val) => { if (!val) onClose(); }}>
			<DialogContent className="max-w-[480px]">
				<DialogHeader>
					<DialogTitle>
						{isEdit ? "Edit Sub-Status" : "New Sub-Status"}
					</DialogTitle>
					<DialogDescription>
						Define a second-level status under a parent stage.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div>
						<label className="text-sm font-medium text-muted-foreground mb-1.5 block">
							Name
						</label>
						<Input
							value={form.name}
							onChange={(e) =>
								setForm((f) => ({ ...f, name: e.target.value }))
							}
							placeholder="e.g. Blocked"
							disabled={saving}
						/>
					</div>

					<div>
						<label className="text-sm font-medium text-muted-foreground mb-1.5 block">
							Parent Status
						</label>
						<SearchableSelect
							value={form.parent_status}
							onValueChange={(v) =>
								setForm((f) => ({ ...f, parent_status: v }))
							}
							options={PARENT_OPTIONS}
							disabled={saving}
							ariaLabel="Parent status"
						/>
					</div>

					<div>
						<label className="text-sm font-medium text-muted-foreground mb-1.5 block">
							Color
						</label>
						<div className="flex flex-wrap items-center gap-2">
							{COLOR_SWATCHES.map((c) => (
								<button
									key={c}
									type="button"
									onClick={() =>
										setForm((f) => ({ ...f, color: c }))
									}
									className={cn(
										"h-7 w-7 rounded-full border-2 transition",
										form.color === c
											? "border-foreground"
											: "border-transparent",
									)}
									style={{ background: c }}
									aria-label={c}
								/>
							))}
						</div>
						<div className="flex items-center gap-2 pt-3">
							<span className="text-xs text-muted">Preview:</span>
							<Chip
								label={form.name.trim() || "Sub-status"}
								color={form.color}
							/>
						</div>
					</div>
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={onClose}
						disabled={saving}
					>
						Cancel
					</Button>
					<Button onClick={handleSave} disabled={saving}>
						{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
						{isEdit ? "Save Changes" : "Create"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function DeleteDialog({
	subStatus,
	onClose,
	onDeleted,
}: {
	subStatus: TaskSubStatus | null;
	onClose: () => void;
	onDeleted: () => void;
}) {
	const [deleting, setDeleting] = useState(false);

	async function handleDelete() {
		if (!subStatus) return;
		setDeleting(true);
		try {
			await deleteTaskSubStatus(subStatus.id);
			toast.success("Sub-status deleted.");
			onDeleted();
			onClose();
		} catch (err) {
			toast.error(
				(err as Error).message || "Failed to delete sub-status.",
			);
		} finally {
			setDeleting(false);
		}
	}

	return (
		<Dialog
			open={subStatus !== null}
			onOpenChange={(val) => { if (!val) onClose(); }}
		>
			<DialogContent className="max-w-[420px]">
				<DialogHeader>
					<DialogTitle>Delete sub-status</DialogTitle>
					<DialogDescription>
						Remove "{subStatus?.name}" from this workspace. Tasks
						using it will be cleared. This cannot be undone.
					</DialogDescription>
				</DialogHeader>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={onClose}
						disabled={deleting}
					>
						Cancel
					</Button>
					<Button
						variant="destructive"
						onClick={handleDelete}
						disabled={deleting}
					>
						{deleting && (
							<Loader2 className="h-4 w-4 animate-spin mr-2" />
						)}
						Delete
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export function TaskSubStatusesSection() {
	const [items, setItems] = useState<TaskSubStatus[]>([]);
	const [loading, setLoading] = useState(true);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editing, setEditing] = useState<TaskSubStatus | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<TaskSubStatus | null>(null);
	const { can } = usePermission();
	const canManage = can("Manage role permissions");

	async function refresh() {
		setLoading(true);
		try {
			const data = await listTaskSubStatuses();
			setItems(data);
		} catch (err) {
			toast.error(
				(err as Error).message || "Failed to load sub-statuses.",
			);
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		refresh();
	}, []);

	function openCreate() {
		setEditing(null);
		setDialogOpen(true);
	}

	function openEdit(item: TaskSubStatus) {
		setEditing(item);
		setDialogOpen(true);
	}

	return (
		<>
			<SectionBlock
				title="Task Sub-Status"
				description="Manage second-level statuses grouped under a parent stage."
			>
				<div className="flex items-center justify-between mb-4">
					<div />
					<PermissionGate feature="Manage role permissions">
						<Button size="sm" onClick={openCreate}>
							<Plus className="h-3.5 w-3.5" />
							Add Sub-Status
						</Button>
					</PermissionGate>
				</div>

				<div className="rounded-lg border border-border overflow-hidden">
					<table className="w-full text-sm">
						<thead>
							<tr className="border-b border-border bg-muted-subtle">
								{["Sub-Status", "Parent Status", "Key", "Action"].map(
									(h, i) => (
										<th
											key={h}
											className={cn(
												"px-4 py-2.5 text-xs font-medium text-muted",
												i === 3 ? "text-right" : "text-left",
											)}
										>
											{h}
										</th>
									),
								)}
							</tr>
						</thead>
						<tbody>
							{loading && (
								<tr>
									<td
										colSpan={4}
										className="px-4 py-6 text-center"
									>
										<div className="flex items-center justify-center gap-2 text-muted">
											<Loader2 className="h-4 w-4 animate-spin" />
											<span className="text-sm">
												Loading sub-statuses...
											</span>
										</div>
									</td>
								</tr>
							)}
							{!loading && items.length === 0 && (
								<tr>
									<td
										colSpan={4}
										className="px-4 py-6 text-center text-sm text-muted"
									>
										No sub-statuses yet.
									</td>
								</tr>
							)}
							{!loading &&
								items.map((t) => (
									<tr
										key={t.id}
										className="border-b border-border last:border-0 hover:bg-muted-subtle transition-colors"
									>
										<td className="px-4 py-3">
											<Chip label={t.name} color={t.color} />
										</td>
										<td className="px-4 py-3">
											<Chip
												label={parentLabel(t.parent_status)}
												color={parentColor(t.parent_status)}
											/>
										</td>
										<td className="px-4 py-3">
											<code className="text-xs text-muted bg-muted-subtle px-1.5 py-0.5 rounded">
												{t.key}
											</code>
										</td>
										<td className="px-4 py-3 text-right">
											{canManage ? (
												<DropdownMenu>
													<DropdownMenuTrigger asChild>
														<button className="text-muted hover:text-foreground transition-colors p-1 rounded">
															<MoreHorizontal className="h-4 w-4" />
														</button>
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end">
														<DropdownMenuItem
															className="flex items-center gap-2"
															onSelect={() => openEdit(t)}
														>
															<Pencil className="h-3.5 w-3.5" />
															Edit
														</DropdownMenuItem>
														{!t.is_system && (
															<DropdownMenuItem
																className="flex items-center gap-2 text-danger focus:text-danger"
																onSelect={() =>
																	setDeleteTarget(t)
																}
															>
																<Trash2 className="h-3.5 w-3.5" />
																Delete
															</DropdownMenuItem>
														)}
													</DropdownMenuContent>
												</DropdownMenu>
											) : (
												<span />
											)}
										</td>
									</tr>
								))}
						</tbody>
					</table>
				</div>
			</SectionBlock>

			<SubStatusDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				editing={editing}
				onSaved={refresh}
			/>

			<DeleteDialog
				subStatus={deleteTarget}
				onClose={() => setDeleteTarget(null)}
				onDeleted={refresh}
			/>
		</>
	);
}
